<script lang="ts" setup>
import { __ } from '@@/bits/intl'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzLabelInput from '@@/library/v4/components/OzLabelInput.vue'
import { useLtiStore } from '@@/pinia/lti_store'
import { computed, onMounted } from 'vue'

const ltiStore = useLtiStore()

const isValid = computed((): 'yes' | 'no' => {
  return ltiStore.subdomainValidationMessage.length ? 'no' : 'yes'
})

const isContinueButtonDisabled = computed((): boolean => {
  return !ltiStore.subdomain.length || !!ltiStore.subdomainValidationMessage.length
})

onMounted(() => {
  // Show timeout screen 5 seconds earlier than the actual token expiration
  const timeoutScreenDelayInSeconds = ltiStore.stateTokenTimeoutSeconds - 5
  setTimeout(() => {
    ltiStore.setExpiryErrorScreen()
  }, timeoutScreenDelayInSeconds * 1000)
})

function onInput(e): void {
  ltiStore.setSubdomain(e.target.value)

  // Reset the validation message to enable the Continue button when typing on mobile and tablet
  if (ltiStore.subdomainValidationMessage) ltiStore.setSubdomainValidationMessage('')
}
</script>

<template>
  <div class="relative flex flex-col items-center tablet-landscape:items-start w-full h-full">
    <main
      :class="[
        'fixed bottom-0',
        'flex flex-col',
        'box-border',
        'w-full',
        'p-4 pb-6',
        'tablet-portrait:relative tablet-portrait:w-[375px] tablet-portrait:p-0 tablet-portrait:px-4 tablet-portrait:mt-16',
        'tablet-landscape:ml-auto tablet-landscape:mr-[calc(0.5*100vw)] tablet-landscape:rtl:mr-auto tablet-landscape:rtl:ml-[calc(0.5*100vw)]',
      ]"
    >
      <div class="mb-5 text-dark-text-100 dark:text-light-text-100">
        <p class="w-56 text-login-heading-1 font-semibold px-2 mb-1">
          {{ __('Connect to Backpack') }}
        </p>
        <p class="text-login-body px-2">
          {{ __('Enter your organization domain') }}
        </p>
      </div>

      <form novalidate class="flex flex-col" @submit.prevent="ltiStore.verifySubdomain">
        <!-- Always be LTR since URLs are always in english -->
        <OzLabelInput
          dir="ltr"
          :input-descriptor-id="'subdomain-input-descriptor'"
          :input-descriptor="
            __(
              'You only need to connect once per deployment for all users. Only a teacher or owner can set up the connection.',
            )
          "
          input-suffix=".padlet.org"
          class="mb-4"
        >
          <template #label>
            <label
              for="subdomain-input"
              :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
            >
              {{ __('Domain') }}
            </label>
          </template>

          <template #input>
            <!--
              Use `model-value` and `@input` here instead of `v-model`
              because the input value doesn't get updated on every keypress on android when using `v-model`
              @see https://github.com/vuejs/vue/issues/9777#issuecomment-478831263
            -->
            <OzInput
              id="subdomain-input"
              name="domain"
              type="text"
              placeholder="hogwarts"
              aria-describedby="subdomain-input-descriptor"
              :disabled="ltiStore.isAwaitingServerResponse"
              :model-value="ltiStore.subdomain"
              :validation-message="ltiStore.subdomainValidationMessage"
              :valid="isValid"
              class="bg-light-ui-100 dark:bg-dark-ui-100"
              @input="onInput"
          /></template>
        </OzLabelInput>

        <div class="flex justify-center tablet-portrait:justify-end tablet-portrait:mb-10">
          <OzContainedButton
            :color-scheme="OzContainedButtonColorScheme.Primary"
            :disabled="ltiStore.isAwaitingServerResponse || isContinueButtonDisabled"
            class="w-full tablet-portrait:w-auto"
            >{{ __('Continue') }}</OzContainedButton
          >
        </div>
      </form>
    </main>
  </div>
</template>
